import { useEffect, useState } from 'react'
import { fbt } from 'fbtee'
import { Video, Music, ExternalLink, Play } from 'lucide-react'
import { detectMedia, mediaHostLabel } from '../lib/media'
import type { MediaProvider } from '../types'

const PROVIDER_BRAND: Partial<Record<MediaProvider, string>> = {
  youtube: 'YouTube',
  soundcloud: 'SoundCloud',
  spotify: 'Spotify',
}

const oembedCache = new Map<string, string>()

function useOembedTitle(provider: MediaProvider, url: string): string | null {
  const [title, setTitle] = useState<string | null>(() => oembedCache.get(url) ?? null)

  useEffect(() => {
    if (provider !== 'youtube') return
    if (oembedCache.has(url)) return
    let cancelled = false
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data?.title) return
        oembedCache.set(url, data.title)
        setTitle(data.title)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [provider, url])

  return title
}

// Provider glyph, reused by the feed card and the set-card badge. Lucide has no
// brand glyph for SoundCloud/Spotify, so those fall back to a generic music icon.
export function ProviderIcon({
  provider,
  className,
}: {
  provider: MediaProvider
  className?: string
}) {
  if (provider === 'youtube') return <Video className={className} />
  if (provider === 'soundcloud' || provider === 'spotify') return <Music className={className} />
  return <ExternalLink className={className} />
}

// A tappable preview of a pasted media link: provider icon + (YouTube)
// thumbnail + host label. Opens in a new tab.
export function MediaLinkCard({ url }: { url: string }) {
  const { provider, thumbnailUrl } = detectMedia(url)
  const oembedTitle = useOembedTitle(provider, url)
  const title = oembedTitle ?? (provider === 'other' ? mediaHostLabel(url) : PROVIDER_BRAND[provider])

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      aria-label={fbt('Open media link', 'Accessible label for a media link card in the set feed')}
      className="flex items-stretch gap-2 rounded-md border bg-muted/30 overflow-hidden hover:bg-muted/50 transition-colors"
    >
      {thumbnailUrl ? (
        <div className="relative w-24 shrink-0 bg-black">
          <img src={thumbnailUrl} alt="" className="w-full h-full object-cover aspect-video" loading="lazy" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-black/60 p-1.5">
              <Play className="size-4 text-on-image fill-on-image" />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center w-12 shrink-0 bg-muted">
          <ProviderIcon provider={provider} className="size-5 text-muted-foreground" />
        </div>
      )}
      <div className="flex flex-col justify-center min-w-0 py-2 pr-2">
        <span className="text-xs font-medium truncate">{title}</span>
        <span className="text-[11px] text-muted-foreground truncate">{mediaHostLabel(url)}</span>
      </div>
    </a>
  )
}
